import { useEffect, useState } from 'react';
import { Input } from '../../components/forms/Input';
import { Button } from '../../components/Button';
import { parseMarkdown } from '../../utils/markdown';
import { usePageTitle } from '../../hooks/usePageTitle';

export function Settings() {
  usePageTitle("Site Settings");
  const [settings, setSettings] = useState({
    site_title: '',
    site_description: '',
    contact_email: '',
    product_page_notes: '',
    product_page_return_policy: '',
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await fetch('/api/settings');
      if (response.ok) {
        const data = await response.json();
        console.log('Loaded settings:', data); // Debug log
        setSettings({
          site_title: data.site_title || '',
          site_description: data.site_description || '',
          contact_email: data.contact_email || '',
          product_page_notes: data.product_page_notes || '',
          product_page_return_policy: data.product_page_return_policy || '',
        });
      } else {
        console.error('Settings fetch failed:', response.status);
      }
    } catch (error) {
      console.error('Error fetching settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setSettings((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setMessage('');

    try {
      const response = await fetch('/api/admin/settings', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(settings),
      });

      if (response.ok) {
        setMessage('Settings saved successfully!');
        document.title = settings.site_title;
        setTimeout(() => setMessage(''), 3000);
      } else {
        setMessage('Failed to save settings');
      }
    } catch (error) {
      console.error('Error saving settings:', error);
      setMessage('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className='flex items-center justify-center py-12'>
        <div className='animate-spin rounded-full h-12 w-12 border-b-2 border-black' />
      </div>
    );
  }

  return (
    <div>
      <h1 className='text-3xl font-bold mb-8'>Site Settings</h1>

      <div className='bg-white p-6 rounded-lg shadow max-w-2xl'>
        <form onSubmit={handleSubmit} className='space-y-4'>
          <Input
            label='Site Title'
            name='site_title'
            value={settings.site_title}
            onChange={handleChange}
            required
          />

          <div>
            <label className='block text-sm font-medium mb-2'>Site Description</label>
            <textarea
              name='site_description'
              value={settings.site_description}
              onChange={handleChange}
              className='w-full px-4 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-black'
              rows={3}
            />
          </div>

          <Input
            label='Contact Email'
            name='contact_email'
            type='email'
            value={settings.contact_email}
            onChange={handleChange}
            required
          />

          <div>
            <label className='block text-sm font-medium mb-2'>Product Page - Important Notes</label>
            <div className='text-xs text-gray-500 mb-2 space-y-1'>
              <p>Markdown formatting supported:</p>
              <div className='flex flex-wrap gap-3'>
                <span><code className='bg-gray-100 px-1 rounded'>**bold**</code></span>
                <span><code className='bg-gray-100 px-1 rounded'>*italic*</code></span>
                <span><code className='bg-gray-100 px-1 rounded'>__underline__</code></span>
                <span><code className='bg-gray-100 px-1 rounded'>[link](url)</code></span>
                <span><code className='bg-gray-100 px-1 rounded'>- list item</code></span>
              </div>
            </div>
            <textarea
              name='product_page_notes'
              value={settings.product_page_notes}
              onChange={handleChange}
              className='w-full px-4 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-black font-mono text-sm'
              rows={6}
              placeholder='Enter important notes with Markdown formatting...'
            />
            {settings.product_page_notes && (
              <div className='mt-4'>
                <h4 className='text-sm font-medium mb-2'>Preview:</h4>
                <div
                  className='prose prose-sm border border-gray-200 rounded p-4 bg-gray-50 max-h-64 overflow-y-auto'
                  dangerouslySetInnerHTML={{ __html: parseMarkdown(settings.product_page_notes) }}
                />
              </div>
            )}
          </div>

          <div>
            <label className='block text-sm font-medium mb-2'>Product Page - Return Policy</label>
            <div className='text-xs text-gray-500 mb-2 space-y-1'>
              <p>Markdown formatting supported:</p>
              <div className='flex flex-wrap gap-3'>
                <span><code className='bg-gray-100 px-1 rounded'>**bold**</code></span>
                <span><code className='bg-gray-100 px-1 rounded'>*italic*</code></span>
                <span><code className='bg-gray-100 px-1 rounded'>__underline__</code></span>
                <span><code className='bg-gray-100 px-1 rounded'>[link](url)</code></span>
                <span><code className='bg-gray-100 px-1 rounded'>- list item</code></span>
              </div>
            </div>
            <textarea
              name='product_page_return_policy'
              value={settings.product_page_return_policy}
              onChange={handleChange}
              className='w-full px-4 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-black font-mono text-sm'
              rows={6}
              placeholder='Enter return policy with Markdown formatting...'
            />
            {settings.product_page_return_policy && (
              <div className='mt-4'>
                <h4 className='text-sm font-medium mb-2'>Preview:</h4>
                <div
                  className='prose prose-sm border border-gray-200 rounded p-4 bg-gray-50 max-h-64 overflow-y-auto'
                  dangerouslySetInnerHTML={{ __html: parseMarkdown(settings.product_page_return_policy) }}
                />
              </div>
            )}
          </div>

          {message && (
            <div
              className={`p-3 rounded ${
                message.includes('success')
                  ? 'bg-green-50 text-green-800'
                  : 'bg-red-50 text-red-800'
              }`}
            >
              {message}
            </div>
          )}

          <Button type='submit' variant='primary' loading={saving}>
            Save Settings
          </Button>
        </form>
      </div>
    </div>
  );
}